//AdminTicketsList.js

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Modal,
  Card,
  Grid,
  IconButton,
  TextField,
  FormControl,
} from "@mui/material";
import { Edit as EditIcon, PhotoAlbumOutlined } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup"; // ← ADDED
import { useSelector } from "react-redux";
import AssignTicket from "./AssignTicket";
import SoftTypography from "components/SoftTypography";
import StarIcon from "@mui/icons-material/Star";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SlideOverPanel from "components/SlideOverPanel";
import AdminAddTicket from "./AdminAddTicket";
import AdminEditTicket from "./AdminEditTicket";
import CustomerDetailContent from "layouts/customers/CustomerDetailContent";
import { RootState } from "store";
import { AxiosError } from "axios";
import DateFormat from "examples/ReuseFunctions/DateFormat";
const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });
const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};
interface Ticket {
  status_tracker: string[];
  employee_name: string;
  employee_phone: string;
  employee_arrival_date: Date | string;
  ticket_id: number;
  asset_type_id: number;
}
interface SpareParts {
  spare_part_id: number;
  spare_part_name: string;
}
interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}
interface TicketStatus {
  status_id: number;
  status_name: string;
}
interface CustomerItem {
  customer_id: number;
  name: string;
}

const sparePartValidationSchema = Yup.object({
  spare_part_id: Yup.mixed().required("Please select a spare part"),
  custom_spare_name: Yup.string().when("spare_part_id", {
    is: "other",
    then: (schema) => schema.required("Custom spare name is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .min(1, "Quantity must be at least 1")
    .required("Quantity is required"),
});

// ← ADDED: Validation schema for arrival date
const arrivalDateValidationSchema = Yup.object({
  employee_arrival_date: Yup.string().required("Arrival date is required"),
  arrival_time: Yup.string().required("Arrival time is required"),
});

const TicketList = () => {
  const navigate = useNavigate();
  const { fetchError, fetchSuccess } = useSnackbar();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get("status_id");
  const [rows, setRows] = React.useState<
    { is_escalated: boolean; [key: string]: any }[]
  >([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState<Ticket | null>(
    null
  );
  const [ticketStatuses, setTicketStatuses] = React.useState<TicketStatus[]>(
    []
  );
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<number | null>(null);
  const [customFilter, setCustomFilter] = React.useState<string | null>(null);
  const [assetTypeFilter, setAssetTypeFilter] = React.useState<number | null>(
    null
  );
  const [dateFilter, setDateFilter] = React.useState<string | null>(null);
  const [customers, setCustomers] = React.useState<CustomerItem[]>([]);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedTicketId, setSelectedTicketId] = React.useState<number | null>(null);
  const [customerFilter, setCustomerFilter] = React.useState<CustomerItem | null>(null);
  const [closedStatusId, setClosedStatusId] = React.useState(null);
  const [customerOverlayOpen, setCustomerOverlayOpen] = React.useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<number | null>(null);

  const clearFilters = () => {
    setStatusFilter(null);
    setCustomFilter(null);
    setAssetTypeFilter(null);
    setDateFilter(null);
    setCustomerFilter(null);
    if (status) {
      navigate(location.pathname, { replace: true });
    }
  };

  const hasActiveFilters =
    statusFilter != null ||
    customFilter != null ||
    assetTypeFilter != null ||
    dateFilter != null ||
    customerFilter != null ||
    status != null;
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = React.useState(false);
  const [spareParts, setSpareParts] = useState<SpareParts[]>([]);
  const user = useSelector((state: RootState) => state.auth.user);
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];
  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
    setRowProps: (row, dataIndex) => {
      const isEscalated = rows[dataIndex]?.is_escalated;
      return {
        style: {
          backgroundColor: isEscalated ? "#ffaba7" : undefined,
        },
      };
    },
  };
  const handleAssignTicketOpen = (
    ticketData: React.SetStateAction<Ticket | null>
  ) => {
    setSelectedTicket(ticketData);
    setIsOpen(true);
  };
  const handleModalOpen = (ticketData: React.SetStateAction<Ticket | null>) => {
    setSelectedTicket(ticketData);
    setIsModalOpen(true);
  };
  const handleRequestModalOpen = (
    ticketData: React.SetStateAction<Ticket | null>
  ) => {
    setSelectedTicket(ticketData);
    setIsRequestModalOpen(true);
  };
  const handleRequestModalClose = () => {
    setIsRequestModalOpen(false);
  };
  const handleModalClose = () => {
    setIsModalOpen(false);
  };
  const handleAssignTicketClose = () => {
    setIsOpen(false);
  };
  const fetchData = React.useCallback(async () => {
    try {
      const endpoint = user?.Role?.includes("supervisor")
        ? `/tickets/supervisor/${user?.userId}`
        : "/tickets";
      const response = await axios.get(endpoint, {
        params: {
          status_id: statusFilter ?? status,
          filters: {
            customFilter: customFilter,
            dateFilter: dateFilter,
            asset_type_id: assetTypeFilter,
            customer_id: customerFilter?.customer_id,
          },
        },
      });
      const filteredData =
        !statusFilter && !status && closedStatusId
          ? response?.data?.list.filter(
              (ticket: { status_id: number }) =>
                ticket.status_id !== closedStatusId
            )
          : response?.data?.list;
      const sortedData = filteredData.sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const mappedData = sortedData.map((ticket: { title: string }) => ({
        ...ticket,
        title: ticket.title || "-",
      }));
      setRows(mappedData || []);
    } catch (error) {
      console.error("Error: ", error);
    }
  }, [
    statusFilter,
    status,
    customFilter,
    dateFilter,
    assetTypeFilter,
    customerFilter,
    closedStatusId,
  ]);
  React.useEffect(() => {
    fetchData();
  }, [
    fetchData,
    statusFilter,
    customFilter,
    dateFilter,
    assetTypeFilter,
    customerFilter,
  ]);
  const fetchStatus = async () => {
    try {
      const [statusResponse, assetTypesData, customersData] = await Promise.all([
        axios.get("/ticket-statuses"),
        axios.get("/asset-types"),
        axios.get("/customer"),
      ]);
      setTicketStatuses(statusResponse?.data);
      setAssetTypes(assetTypesData?.data?.list);
      setCustomers(customersData?.data?.list ?? []);
      const closedStatus = statusResponse?.data.find(
        (s: { status_name: string }) => s.status_name.toLowerCase() === "closed"
      );
      if (closedStatus) {
        setClosedStatusId(closedStatus.status_id);
      }
    } catch (error) {
      console.error("Error: ", error);
    }
  };
  React.useEffect(() => {
    fetchStatus();
  }, []);
  const fetchSpareParts = useCallback(async () => {
    try {
      const response = await axios.get("/spare-parts", { params: { is_active: 1 } });
      const parts = Array.isArray(response?.data) ? response.data : [];
      setSpareParts(parts.filter((s) => Number(s.is_active) === 1));
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, []);
  useEffect(() => {
    fetchSpareParts();
  }, [fetchSpareParts]);
  const handleDeEscalationUpdate = async (ticket: { ticket_id: number }) => {
    const ticketData = {
      is_escalated: 0,
    };
    try {
      await axios.put(`/tickets/escalation/${ticket?.ticket_id}`, ticketData);
      fetchData();
      fetchSuccess("Ticket de-escalated Successfully");
    } catch (err) {
      console.error("Error: ", err);
      fetchError("Unable to de-escalate Ticket");
    }
  };
  const isOnDemand = (ticket: { end_date: string | number | Date }) => {
    if (!ticket?.end_date) return true;
    const today = new Date().setHours(0, 0, 0, 0);
    const endDate = new Date(ticket.end_date).setHours(0, 0, 0, 0);
    return endDate < today;
  };
  const renderTitle = (
    value: string,
    tableMeta: any,
    isOnDemand: (ticket: any) => boolean
  ) => {
    const { currentTableData, rowIndex, tableData } = tableMeta;
    const ticket = tableData[currentTableData[rowIndex]?.index];
    const showOnDemand = isOnDemand(ticket);

    return (
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          maxWidth: "100%",
        }}
      >
        {value}{" "}
        {showOnDemand && (
          <span style={{ color: "red", fontWeight: "bold" }}>(On-Demand)</span>
        )}
      </span>
    );
  };
  const openCustomerOverlay = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setCustomerOverlayOpen(true);
  };

  const renderCustomerName = (value: string, tableMeta: any) => {
    const { currentTableData, rowIndex, tableData } = tableMeta;
    const data = tableData[currentTableData[rowIndex]?.index];
    const customerId = data?.customer_id;
    const name = value ?? data?.customer_name ?? "";

    const nameContent = (
      <>
        {name}
        {data?.premium_customer ? (
          <StarIcon
            sx={{
              fontSize: 15,
              color: "#FFD700",
              filter: "drop-shadow(0px 0px 2px rgba(255, 215, 0, 0.8))",
              verticalAlign: "middle",
              ml: 0.25,
            }}
          />
        ) : null}
      </>
    );

    if (customerId != null) {
      return (
        <span className="subscription">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openCustomerOverlay(customerId);
            }}
            style={{
              cursor: "pointer",
              color: "#1976d2",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              textDecoration: "underline",
            }}
          >
            {nameContent}
          </button>
        </span>
      );
    }

    return data?.premium_customer ? (
      <span className="subscription">{nameContent}</span>
    ) : (
      value
    );
  };
  const renderImages = (
    _: any,
    tableMeta: any,
    navigate: (path: string) => void
  ) => {
    const { currentTableData, rowIndex, tableData } = tableMeta;
    const ticketData = tableData[currentTableData[rowIndex]?.index];

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {ticketData?.multimedia?.length > 0 ? (
          <IconButton
            aria-label="photos"
            onClick={() =>
              navigate(`/admin/ticket-images/${ticketData.ticket_id}`)
            }
          >
            <PhotoAlbumOutlined sx={{ color: "#1976d2" }} />
          </IconButton>
        ) : (
          "No Media"
        )}
      </Box>
    );
  };
  const handleEditTicket = (ticketId: number) => {
    setSelectedTicketId(ticketId);
    setEditPanelOpen(true);
  };

  const renderActions = (
    _: any,
    tableMeta: any,
    actions: {
      navigate: Function;
      onEditTicket: (ticketId: number) => void;
      handleAssignTicketOpen: Function;
      handleRequestModalOpen: Function;
      handleModalOpen: Function;
      handleDeEscalationUpdate: Function;
    }
  ) => {
    const { currentTableData, rowIndex, tableData } = tableMeta;
    const ticketData = tableData[currentTableData[rowIndex]?.index];

    const actionButtonSx = {
      width: 118,
      minWidth: 118,
      height: 28,
      minHeight: 28,
      maxHeight: 28,
      padding: "0 6px",
      fontSize: "0.7rem",
      textTransform: "none",
      whiteSpace: "nowrap",
    } as const;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 0.5,
        }}
      >
        <IconButton
          size="small"
          sx={{ width: 28, height: 28 }}
          onClick={() => actions.onEditTicket(ticketData.ticket_id)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ width: 28, height: 28 }}
          onClick={() =>
            actions.navigate(`/admin/ticket-view/${ticketData.ticket_id}`)
          }
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
        {![6, 7, 8, 9].includes(ticketData?.status_id) && (
          <Button
            className="assign-btn"
            sx={actionButtonSx}
            size="small"
            variant="contained"
            onClick={() => actions.handleRequestModalOpen(ticketData)}
          >
            Request Spare Part
          </Button>
        )}
        <Button
          className="assign-btn"
          sx={actionButtonSx}
          size="small"
          variant="contained"
          onClick={() => actions.handleAssignTicketOpen(ticketData)}
        >
          {ticketData?.assigned_employee_id ? "Re-Assign" : "Assign"}
        </Button>
        {ticketData?.status_id === 2 && (
          <Button
            sx={actionButtonSx}
            size="small"
            variant="contained"
            onClick={() => actions.handleModalOpen(ticketData)}
          >
            Arrival Date
          </Button>
        )}
        {ticketData?.is_escalated ? (
          <Button
            className="assign-btn"
            sx={actionButtonSx}
            size="small"
            variant="contained"
            onClick={() => {
              handleDeEscalationUpdate(ticketData);
            }}
          >
            De-Escalate
          </Button>
        ) : null}
      </Box>
    );
  };

  const leftCellProps = { style: { textAlign: "left" as const } };

  const columns: MUIDataTableColumn[] = [
    {
      name: "title",
      label: "Title",
      options: {
        filter: false,
        setCellProps: () => leftCellProps,
        customBodyRender: (value, tableMeta) =>
          renderTitle(value, tableMeta, isOnDemand),
      },
    },
    {
      name: "customer_name",
      label: "Customer Name",
      options: {
        filter: false,
        setCellProps: () => leftCellProps,
        customBodyRender: renderCustomerName,
      },
    },
    {
      name: "employee_name",
      label: "Assignee",
      options: {
        filter: false,
        setCellProps: () => leftCellProps,
        customBodyRender: (value: string | null) => value ?? "Unassigned",
      },
    },
    {
      name: "id",
      label: "Images",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => leftCellProps,
        customBodyRender: (value, tableMeta) =>
          renderImages(value, tableMeta, navigate),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => leftCellProps,
        customBodyRender: (value, tableMeta) =>
          renderActions(value, tableMeta, {
            navigate,
            onEditTicket: handleEditTicket,
            handleAssignTicketOpen,
            handleRequestModalOpen,
            handleModalOpen,
            handleDeEscalationUpdate,
          }),
      },
    },
  ];

  const filterOptions = ["Week", "Month", "Year"];
  let date: Date | null = new Date();
  switch (customFilter) {
    case "Week":
      date.setDate(date.getDate() - 7);
      break;
    case "Month":
      date.setDate(date.getDate() - 30);
      break;
    case "Year":
      date.setDate(date.getDate() - 365);
      break;
    default:
      date = null;
  }
  const currentTime = DateFormat();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: 12,
          }}
          className="manage-tickets-header"
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Customised Date Filter
              </SoftTypography>
              <Autocomplete
                options={filterOptions || []}
                getOptionLabel={(option) => option}
                value={customFilter}
                onChange={(event, value) => setCustomFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            {date && (
              <Box component="span" sx={{ alignSelf: "center", fontSize: "0.875rem" }}>
                From: {date.toISOString().slice(0, 19).replace("T", " ")} To:{" "}
                {new Date().toISOString().slice(0, 19).replace("T", " ")}
              </Box>
            )}
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Created Date
              </SoftTypography>
              <TextField
                className="date-field"
                name="start_date"
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={dateFilter ?? ""}
                onChange={(event) => setDateFilter(event.target.value || null)}
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Status
              </SoftTypography>
              <Autocomplete
                options={ticketStatuses || []}
                getOptionLabel={(option) => option.status_name}
                value={
                  ticketStatuses.find((s) => s.status_id === statusFilter) ?? null
                }
                onChange={(event, value) => {
                  setStatusFilter(value?.status_id ?? null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Asset Types
              </SoftTypography>
              <Autocomplete
                options={assetTypes || []}
                getOptionLabel={(option) => option.asset_type_name}
                value={
                  assetTypes.find(
                    (a) => a.asset_type_id === assetTypeFilter
                  ) ?? null
                }
                onChange={(event, value) => {
                  setAssetTypeFilter(value?.asset_type_id ?? null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Customer
              </SoftTypography>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option.name ?? ""}
                value={customerFilter}
                onChange={(_, value) => setCustomerFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select customer"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            {hasActiveFilters && (
              <Button
                className="success-btn"
                color="success"
                variant="outlined"
                size="small"
                onClick={clearFilters}
              >
                Clear Filter
              </Button>
            )}
            <Button
              className="success-btn"
              color="success"
              onClick={() => setAddPanelOpen(true)}
              sx={{ marginLeft: "auto", flexShrink: 0 }}
            >
              Add Ticket
            </Button>
          </Box>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Tickets"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
      {isOpen && (
        <AssignTicket
          assignTicketData={selectedTicket}
          isOpen={isOpen}
          onClose={handleAssignTicketClose}
          fetchData={fetchData}
        />
      )}
      {isModalOpen && (
        <Modal open={isModalOpen} onClose={handleModalClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }}
          >
            <Formik
              initialValues={{
                employee_arrival_date: "",
                arrival_time: "",
                reason_for_delay: "",
              }}
              validationSchema={arrivalDateValidationSchema} // ← ADDED
              onSubmit={async (values) => {
                const ticketData = {} as {
                  status_tracker?: string;
                  employee_arrival_date?: string;
                };
                const trackerData = StatusTracker({
                  previousData: Array.isArray(selectedTicket?.status_tracker)
                    ? JSON.stringify(selectedTicket.status_tracker)
                    : selectedTicket?.status_tracker ?? null,
                  message: values.reason_for_delay
                    ? `Engineer will arrive on ${values.employee_arrival_date} at ${values.arrival_time} due to ${values.reason_for_delay}`
                    : `Engineer will arrive on ${values.employee_arrival_date} at ${values.arrival_time}`,
                  statusName: "Todo",
                  status_id: 3,
                  createdby: user?.name ?? "",
                  employeeName: selectedTicket?.employee_name ?? "",
                  employeePhone: selectedTicket?.employee_phone ?? "",
                  arraivalDate: currentTime,
                });

                ticketData.status_tracker = trackerData;
                ticketData.employee_arrival_date = `${values.employee_arrival_date}T${values.arrival_time}`;
                try {
                  await axios.put(`/tickets/${selectedTicket?.ticket_id}`, {
                    ticketData,
                  });
                  fetchData();
                  handleModalClose();
                  fetchSuccess("Arrival date updated successfully");
                } catch (err) {
                  fetchError("Unable to Update Ticket");
                  throw err;
                }
              }}
            >
              {({ errors, touched, setFieldValue, values }) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Arrival Date
                        </SoftTypography>
                        <Field
                          className="date-field"
                          name="employee_arrival_date"
                          as={TextField}
                          InputProps={{
                            inputProps: {
                              min: formattedToday,
                            },
                          }}
                          type="date"
                          fullWidth
                          variant="outlined"
                          error={
                            !!errors.employee_arrival_date &&
                            touched.employee_arrival_date
                          }
                          helperText={
                            touched.employee_arrival_date &&
                            errors.employee_arrival_date
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Arrival Time
                        </SoftTypography>
                        <Field
                          className="time-field"
                          name="arrival_time"
                          as={TextField}
                          type="time"
                          fullWidth
                          variant="outlined"
                          error={!!errors.arrival_time && touched.arrival_time}
                          helperText={
                            touched.arrival_time && errors.arrival_time
                          }
                        />
                      </FormControl>
                    </Grid>
                    {selectedTicket?.employee_arrival_date && (
                      <Grid item xs={12}>
                        <SoftBox>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Reason For Delay
                          </SoftTypography>
                          <TextField
                            name="reason_for_delay"
                            variant="outlined"
                            onChange={(event) => {
                              const data = event.target.value;
                              setFieldValue("reason_for_delay", data || null);
                            }}
                            fullWidth
                            error={
                              !!errors.reason_for_delay &&
                              touched.reason_for_delay
                            }
                            helperText={
                              touched.reason_for_delay &&
                              errors.reason_for_delay
                            }
                          />
                        </SoftBox>
                      </Grid>
                    )}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item>
                          <SoftButton
                            className="success-btn"
                            variant="gradient"
                            color="success"
                            type="submit"
                          >
                            Save
                          </SoftButton>
                        </Grid>
                        <Grid item>
                          <SoftButton
                            className="cancel-btn"
                            variant="gradient"
                            color="warning"
                            onClick={handleModalClose}
                          >
                            Cancel
                          </SoftButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Modal>
      )}
      {isRequestModalOpen && (
        <Modal open={isRequestModalOpen} onClose={handleRequestModalClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              maxWidth: 500,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }}
          >
            <Formik
              enableReinitialize
              initialValues={{
                ticket_id: selectedTicket?.ticket_id || "",
                requested_by: user?.userId,
                asset_type_id: selectedTicket?.asset_type_id || "",
                spare_part_id: "",
                custom_spare_name: "",
                quantity: "",
              }}
              validationSchema={sparePartValidationSchema} 
              onSubmit={async (values) => {
                try {
                  await axios.post(`/spare-part-request/create`, {
                    ticket_id: selectedTicket?.ticket_id,
                    requested_by: user?.userId,
                    asset_type_id: selectedTicket?.asset_type_id,
                    spare_part_id:
                      values.spare_part_id === "other"
                        ? null
                        : values.spare_part_id || null,
                    custom_spare_name:
                      values.spare_part_id === "other"
                        ? values.custom_spare_name
                        : null,
                    quantity: values.quantity || 1,
                  });
                  const spareName =
                    values.spare_part_id === "other"
                      ? values.custom_spare_name
                      : spareParts.find(
                          (s) =>
                            s.spare_part_id === Number(values.spare_part_id)
                        )?.spare_part_name || "spare part";
                  const message = `Spare part request created for ${spareName}`;
                  const trackerData = StatusTracker({
                    previousData: Array.isArray(selectedTicket?.status_tracker)
                      ? selectedTicket.status_tracker.join(",")
                      : selectedTicket?.status_tracker ?? null,
                    message: message,
                    statusName: "On Hold",
                    status_id: 4,
                    createdby: user?.name ?? "",
                    employeeName: selectedTicket?.employee_name ?? "",
                    employeePhone: selectedTicket?.employee_phone ?? "",
                    arraivalDate: currentTime,
                  });

                  await axios.put(`/tickets/${selectedTicket?.ticket_id}`, {
                    ticketData: { status_tracker: trackerData },
                  });
                  fetchData();
                  handleRequestModalClose();
                  fetchSuccess("Spare part request created successfully");
                } catch (err) {
                  console.log("err", err);
                  fetchError("Unable to create Spare Part Request");
                }
              }}
            >
              {({ errors, touched, setFieldValue, values }) => {
                const spareOptions = [
                  ...(spareParts || []),
                  { spare_part_id: "other", spare_part_name: "Other" },
                ];
                return (
                  <Form>
                    <Grid container spacing={2}>
                      {/* Spare Part Dropdown */}
                      <Grid item xs={12}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Spare Part
                        </SoftTypography>
                        <Autocomplete
                          options={spareOptions}
                          getOptionLabel={(option) =>
                            option.spare_part_name || ""
                          }
                          value={
                            spareOptions.find(
                              (s) => s.spare_part_id === values.spare_part_id
                            ) || null
                          }
                          onChange={(e, value) => {
                            setFieldValue(
                              "spare_part_id",
                              value?.spare_part_id || ""
                            );
                            if (value?.spare_part_id !== "other") {
                              setFieldValue("custom_spare_name", "");
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select spare part"
                              error={
                                !!errors.spare_part_id && touched.spare_part_id
                              }
                              helperText={
                                touched.spare_part_id && errors.spare_part_id
                                  ? String(errors.spare_part_id)
                                  : ""
                              }
                            />
                          )}
                        />
                      </Grid>
                      {values.spare_part_id === "other" && (
                        <Grid item xs={12}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Custom Spare Name
                          </SoftTypography>
                          <Field
                            name="custom_spare_name"
                            as={TextField}
                            fullWidth
                            variant="outlined"
                            placeholder="Enter custom spare name"
                            error={
                              !!errors.custom_spare_name &&
                              touched.custom_spare_name
                            }
                            helperText={
                              touched.custom_spare_name &&
                              errors.custom_spare_name
                            }
                          />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Quantity
                        </SoftTypography>
                        <Field
                          name="quantity"
                          as={TextField}
                          fullWidth
                          variant="outlined"
                          type="number"
                          placeholder="Enter quantity"
                          inputProps={{ min: 1 }}
                          error={!!errors.quantity && touched.quantity}
                          helperText={touched.quantity && errors.quantity}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item>
                            <SoftButton
                              className="success-btn"
                              variant="gradient"
                              color="success"
                              type="submit"
                            >
                              Save
                            </SoftButton>
                          </Grid>
                          <Grid item>
                            <SoftButton
                              className="cancel-btn"
                              variant="gradient"
                              color="warning"
                              onClick={handleRequestModalClose}
                            >
                              Cancel
                            </SoftButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Box>
        </Modal>
      )}
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Ticket"
        width={1000}
      >
        <AdminAddTicket
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedTicketId(null);
        }}
        title="Edit Ticket"
        width={1000}
      >
        {selectedTicketId != null && (
          <AdminEditTicket
            id={String(selectedTicketId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedTicketId(null);
            }}
          />
        )}
      </SlideOverPanel>
      <SlideOverPanel
        open={customerOverlayOpen}
        onClose={() => {
          setCustomerOverlayOpen(false);
          setSelectedCustomerId(null);
        }}
        title="Customer details"
        width={900}
      >
        {selectedCustomerId != null && (
          <CustomerDetailContent customerId={String(selectedCustomerId)} />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};
export default TicketList;
